// Centralized API client with session token management

const API_BASE = process.env.REACT_APP_API_URL || '';

// ─── Token storage ───────────────────────────────────────────────────────

export function getToken()  { return localStorage.getItem('am_session_token'); }
export function setToken(t) { localStorage.setItem('am_session_token', t); }
export function clearToken() {
  localStorage.removeItem('am_session_token');
  localStorage.removeItem('am_consumer_id');
  localStorage.removeItem('am_creator_id');
}

export function getSession() {
  const token = getToken();
  if (!token) return null;
  try {
    // Decode JWT payload (no verification — server does that)
    const payload = JSON.parse(atob(token.split('.')[1]));
    // Check expiry
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      clearToken();
      return null;
    }
    return payload;
  } catch {
    clearToken();
    return null;
  }
}

export function isSignedIn() { return !!getSession(); }

// ─── Authenticated fetch ─────────────────────────────────────────────────

export async function authFetch(path, options = {}) {
  const token = getToken();
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.body && typeof options.body === 'string') {
    headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  }
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  // Auto sign-out on 401 (expired token)
  if (res.status === 401 && token) {
    clearToken();
    window.dispatchEvent(new CustomEvent('auth-expired'));
  }
  return res;
}

// ─── Auth helpers ────────────────────────────────────────────────────────

export async function signIn(email, password) {
  const res = await fetch(`${API_BASE}/v1/auth/signin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (data.token) {
    setToken(data.token);
    localStorage.setItem('am_consumer_id', data.consumerId);
    if (data.creatorId) localStorage.setItem('am_creator_id', data.creatorId);
  }
  return { ok: res.ok, ...data };
}

export async function signUp({ name, email, password }) {
  const res = await fetch(`${API_BASE}/v1/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password }),
  });
  const data = await res.json();
  if (data.token) {
    setToken(data.token);
    localStorage.setItem('am_consumer_id', data.consumerId);
  }
  return { ok: res.ok, ...data };
}

export async function signInWithGoogle(credential) {
  const res = await fetch(`${API_BASE}/v1/auth/google`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });
  const data = await res.json();
  if (data.token) {
    setToken(data.token);
    localStorage.setItem('am_consumer_id', data.consumerId);
    if (data.creatorId) localStorage.setItem('am_creator_id', data.creatorId);
  }
  return { ok: res.ok, ...data };
}

export function signOut() {
  clearToken();
}

// ─── Jobs API ───────────────────────────────────────────────────────────

export async function submitJob(buildSlug, jobType, params = {}) {
  const res = await authFetch(`/v1/jobs/${buildSlug}`, {
    method: 'POST',
    body: JSON.stringify({ jobType, params }),
  });
  return res.json();
}

export async function pollJob(jobId) {
  const res = await authFetch(`/v1/jobs/${jobId}`);
  return res.json();
}

export async function cancelJob(jobId) {
  const res = await authFetch(`/v1/jobs/${jobId}/cancel`, { method: 'POST' });
  return res.json();
}

export async function getMyJobs() {
  const res = await authFetch('/v1/consumer/jobs');
  return res.json();
}

// ─── File Upload API ───────────────────────────────────────────────────

export async function callBuildWithFiles(slug, params = {}, files = []) {
  const form = new FormData();
  if (params && Object.keys(params).length > 0) {
    form.append('_params', JSON.stringify(params));
  }
  for (const file of files) {
    form.append('file', file, file.name);
  }
  // Don't set Content-Type — browser adds multipart boundary automatically
  const token = getToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}/v1/call/${slug}`, {
    method: 'POST',
    headers,
    body: form,
  });
  return res;
}

// ─── Search & Discovery ──────────────────────────────────────────────────

export async function searchBuilds(q, filters = {}) {
  const params = new URLSearchParams({ q, ...filters });
  const res = await fetch(`${API_BASE}/v1/search?${params}`);
  return res.json();
}

export async function getTrending(days = 7) {
  const res = await fetch(`${API_BASE}/v1/trending?days=${days}`);
  return res.json();
}

// ─── Builder Analytics ───────────────────────────────────────────────────

export async function getBuilderAnalytics(buildSlug, days = 30) {
  const res = await authFetch(`/v1/builder/builds/${buildSlug}/analytics?days=${days}`);
  return res.json();
}

// ─── Webhooks ────────────────────────────────────────────────────────────

export async function getWebhooks() {
  const res = await authFetch('/v1/webhooks');
  return res.json();
}

export async function createWebhook(url, events) {
  const res = await authFetch('/v1/webhooks', {
    method: 'POST', body: JSON.stringify({ url, events }),
  });
  return res.json();
}

export async function deleteWebhook(id) {
  const res = await authFetch(`/v1/webhooks/${id}`, { method: 'DELETE' });
  return res.json();
}

// ─── Pipelines ───────────────────────────────────────────────────────────

export async function listPipelines() {
  const res = await fetch(`${API_BASE}/v1/pipelines`);
  return res.json();
}

export async function runPipeline(slug, input = {}) {
  const res = await authFetch(`/v1/pipelines/${slug}/run`, {
    method: 'POST', body: JSON.stringify({ input }),
  });
  return res.json();
}

// ─── Pricing Tiers ��─────────────────────────────────────────────────────

export default API_BASE;
